<template>
	<v-card id="dump-btn"
		class="d-flex flex-column justify-space-around"
		:class="{'is-focused': focused}"
		height="100%"
		elevation="1"
		@mouseover="addFocus()"
		@mouseleave="removeFocus($event)">
		<div class="available-buffer-bar" v-if="isDumpAvailable && availableDumpBuffer"
			:class="{
				maximum: availableBufferPercentage > 66.6,
				middle: availableBufferPercentage > 33.3,
				'theme--dark': darkMode
			}"
			:style="{
				height: `${availableBufferPercentage}%`,
				'border-top-left-radius': availableBufferPercentage === 100 ? 'inherit': 0,
				'border-top-right-radius': availableBufferPercentage === 100 ? 'inherit' : 0
			}">
		</div>
		<v-btn icon
			size="large"
			block
			rounded="0"
			:ripple="false"
			variant="text"
			height="100%"
			:disabled="!isDumpAvailable || isDumpPending || !isDumpMoreThanMinLimit"
			@click="dumpBuffer">
			<div>
				<div class="py-8">
					<div class="d-flex align-center justify-center">
						<v-icon icon="mdi-replay" size="68"/>
						<span id="dump-seconds">{{ dumpValue }}</span>
					</div>
					<div class="text-h3 font-weight-bold">
						DUMP
					</div>
				</div>
				<div v-if="isDumpAvailable && availableDumpBuffer" class="text-h5">
					{{ availableDumpBuffer }} SECS
				</div>
			</div>
		</v-btn>
	</v-card>
</template>

<script>
/*Libraries*/
import WorkerTimer from "@/plugins/WorkerTimer";
import emitter from "@/emitter";

/*Mixins*/
import SequencerPlayerStudioStatusMixin from "@/mixins/SequencerPlayerStudioStatus";

export default {
	name: "DumpButton",
	data() {
		return {
			focused: false
		};
	},

	mixins: [SequencerPlayerStudioStatusMixin],

	computed: {
		selectedStation() {
			return this.$store.getters.liveModeSelectedStation();
		},

		dumpValue() {
			return this.selectedStation?.StartLiveSettings?.ManualDumpDuration ?? 7;
		},

		isDumpAvailable() {
			return this.selectedStation &&
				   (this.playerIsLive || this.isSyndicationOriginated || this.isSplitMasterGoLiveDisabled) &&
				   this.connectedHost &&
				   this.userIsHost;
		},

		availableDumpBuffer() {
			return this.$store.getters.getLivePlayerSnapshot()?.dumpBufferDuration ?? 0;
		},

		isDumpPending() {
			return this.$store.getters.dumpBufferPendingStatus();
		},

		availableBufferPercentage() {
			const totalBuffer = this.$store.getters.getLivePlayerSnapshot()?.dumpBufferMaxDuration ?? 0;
			return (this.availableDumpBuffer * 100) / totalBuffer;
		},

		isDumpMoreThanMinLimit() {
			return this.$store.getters.getLivePlayerSnapshot()?.dumpBufferDuration > 4.5
		},

		darkMode() {
			return this.$store.getters.darkMode();
		}
	},

	watch: {
		isDumpPending(value) {
			const that = this;
			if (value) {
				WorkerTimer.setTimeout(() => {
					if (that.$store.getters.dumpBufferPendingStatus()) {
						that.$store.commit(
							"SHOW_NOTIFICATION_WITH_TYPE",
							{message: "An error occurred when trying to dump the buffer."}
						);
						that.$store.commit("setDumpBufferPendingStatus", false);
					}
				}, 5000);
			}
		}
	},

	methods: {
		dumpBuffer() {
			if (!this.isDumpAvailable || this.isDumpPending || !this.isDumpMoreThanMinLimit) {
				return;
			}

			const message = {
				action: "Dump",
				body: {
					dumpSeconds: this.dumpValue
				}
			}

			this.$store.dispatch("livePlayerActionRequest", message);
			this.$store.commit("setDumpBufferPendingStatus", true);
		},

		removeFocus(ev) {
			if (this.$el.contains(ev.target)) {
				this.focused = false;
			}
		},

		addFocus() {
			if (!this.isDumpAvailable || this.isDumpPending || !this.isDumpMoreThanMinLimit) {
				return;
			}
			this.focused = true;
		}
	},

	mounted() {
		emitter.on("dump:pressed", this.dumpBuffer);
	},

	created() {
		// Remove focus from dump buffer when clicking outside of it
		window.addEventListener("click", this.removeFocus);
	},

	beforeUnmount() {
		window.removeEventListener("click", this.removeFocus);
	}
};
</script>

<style lang="scss" scoped>
#dump-btn {
	color: $grey-500;
	font-weight: bold;

	&.is-focused {
		border: 2px solid $ihm-red;
	}

	button {
		border-radius: inherit;

		&.v-theme--dark {
			color: #a9afb2 !important;
		}
	}

	#dump-seconds {
		font-size: 20px;
		font-weight: bold;
		padding-top: 8px;
		position: absolute;
	}

	.available-buffer-bar {
		background: #FDEFED;
		bottom: 0;
		border-radius: inherit;
		height: 0;
		position: absolute;
		transition: height .5s linear;
		width: 100%;

		&.middle {
			background: #FEFCED;
		}

		&.maximum {
			background: #E7F6E9;
		}

		&.theme--dark {
			background: $ihm-grey;
		}
	}
}
</style>
